import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { getRandomInviteShareImageUrl } from 'src/creatives/share';
import { ImageView } from '@play-co/timestep-core/ui';
import { shareLinkToMessenger } from 'src/lib/messenger';
import { analytics } from '@play-co/gcinstant';
import { AB } from 'src/lib/AB';
import { shareLongLinkToInstagram } from 'src/lib/instagram';

/** Share link to messenger or instagram, return true if worked, false if failed */
async function share(method: 'native' | 'direct', subFeature: string) {
  // Find out user's current platform
  const platform =
    process.env.APP_ID === 'TREMBLY-MESSENGER' ? 'messenger' : 'instagram';

  // Switch share method according to the current platform
  const shareMethod =
    platform === 'messenger' ? shareLinkToMessenger : shareLongLinkToInstagram;

  try {
    const imageUrl = getRandomInviteShareImageUrl();
    await shareMethod({
      text: i18n('social.shareInvite.shareText'),
      title: i18n('social.shareInvite.shareTitle'),
      description: i18n('social.shareInvite.shareDescription'),
      image: imageUrl,
      data: {
        feature: 'share',
        $subFeature: subFeature,
        $creativeAssetID: imageUrl.split('/').pop(),
      },
      chatbot: {
        messageText: i18n('social.shareInvite.shareText'),
        imageUrl,
        buttonText: i18n('social.shareInvite.shareButton'),
      },
      method,
    });
    return true;
  } catch (error) {
    analytics.pushEvent('ShareLinkFailed', {
      errorCode: error.code,
      errorMessage: error.message,
      platform,
      method,
      subFeature,
    });
    return false;
  }
}

/** Give the share/invite rewards and present the reward popup */
async function shareReward() {
  const subFeature = 'invite_share_gems_reward';
  await StateObserver.invoke.claimShareRewards(subFeature);
  await openPopupPromise('popupInviteReward', {
    inviteCount: 1,
    subFeature,
    titleKey: 'social.shareInvite.rewardTitle',
    subtitleKey: 'social.shareInvite.rewardSubtitle',
  });
}

export default class PopupShareInvite extends PopupBasic {
  buttonOkay: ButtonScaleViewWithText;
  buttonCancel: ButtonScaleViewWithText;
  subFeature: string;

  constructor(opts: { superview: View; close: (result: boolean) => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      zIndex: 11000,
      width: 600,
      height: 900,
      darkerBg: true,
      titleIcon: {
        image: 'assets/ui/buruburu/friend_referral_icon.png',
        width: 80,
        height: 80,
      },
      tracking: { feature: 'generic', $subFeature: 'popup-confirmation' },
    });

    this.buttonClose.updateOpts({
      width: 62 * 1.5,
      height: 67 * 1.5,
      y: 0,
    });

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 200,
      size: 30,
      x: 40,
      y: 70,
      zIndex: 1,
      color: '#B07F4C',
    });

    new ImageView({
      width: 528,
      height: 468,
      zIndex: 1,
      centerAnchor: true,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 490,
      superview: this.box,
      image: 'assets/ui/buruburu/share/share_invite_popup.png',
    });

    this.buttonOkay = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      localeText: () => i18n('social.shareInvite.invite'),
      superview: this.box,
      labelOffsetY: -1,
      fontSize: 45,
      font: 'Body',
      x: this.box.style.width / 2,
      y: this.box.style.height - 90,
      width: 300,
      height: 100,
      centerOnOrigin: true,
      onClick: async () => {
        const shared = await this.share();
        opts.close(true);
        if (shared) await shareReward();
      },
      tracking: { feature: 'generic', $subFeature: 'popup-confirmation-ok' },
    });
  }

  init(opts: { subFeature: string }) {
    super.init({
      title: i18n('social.shareInvite.title'),
      message: i18n('social.shareInvite.message'),
    });
    this.subFeature = opts.subFeature;
  }

  private async share() {
    // Default to native share
    let method: 'native' | 'direct' = 'native';

    // Run the share that shares to messenger only
    return share(method, this.subFeature);
  }
}
